
import {
  Box,
  Page,
  VerticalStack,
  HorizontalGrid,
  Text,
  AlphaCard,
  useBreakpoints,
  TextField,
  Button,
  ChoiceList,
  Link,
  InlineError,
  HorizontalStack,
  Icon,
  Image,
  Toast,
  Tabs,
  ExceptionList,
  Divider,
  ButtonGroup,
  Badge,
  Select,
  Avatar
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { ProfileMajor, PlayCircleMajor, ClockMinor, DeleteMajor, FavoriteMajor, Columns3Major, EditMajor } from '@shopify/polaris-icons';
import { useLocation } from 'react-router-dom';
import { AddressElement } from '@stripe/react-stripe-js';

import Instagram from '../../assets/Instagram.svg';
import Youtube from '../../assets/Youtube.svg';
import Tiktok from '../../assets/Tiktok.svg';

import { deleteCaseStudy, saveInfluencerProfileChanges } from '../../firebase';
import { usePartnerUp } from '../../providers/PartnerUpProvider';
import stripeLogo from '../../assets/stripe-logo.png';
import Layout from '../../components/layout';
import PriceModal from '../../components/price-modal';
import SocialMediaModal from '../../components/social-media-modal';
import AddonModal from '../../components/addon-modal';
import { getCountry, getBestAvatar } from '../../utils';

// this is fine since it's a client-side key
const MAPS_KEY = 'AIzaSyACFv1SiRtvQ6IsAwoRJZeiT8c_UZp9pFQ'
//

function getSocialIcon(platform) {
  switch (platform) {
    case 'instagram':
      return <Image width="22" source={Instagram} />
    case 'youtube':
      return <Image width="22" source={Youtube} />
    case 'tiktok':
      return <Image width="22" source={Tiktok} />
    default:
      return <Icon source={ProfileMajor} />
  }
}

function getSocialLabel(platform, socialStats) {
  switch (platform) {
    case 'instagram':
      return '@' + socialStats.atName
    case 'youtube':
      return socialStats.name
    case 'tiktok':
      return socialStats.atName
    default:
      return socialStats.name
  }
}

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Views Per Day (Last 30 Days)',
    },
  },
};

function getTabIndex(tabName) {
  switch (tabName) {
    case 'profile':
      return 0;
    case 'case-studies':
      return 1;
    case 'prices':
      return 2;
    case 'revenue':
      return 3;
    case 'address':
      return 4;
    default:
      return 0;
  }
}

export default function InfluencerProfile() {
  const { smUp } = useBreakpoints();
  const { profile, prices, addons, caseStudies } = usePartnerUp();
  const location = useLocation();

  const searchPararms = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(getTabIndex(searchPararms.get('tab')));

  const [shippingInfo, setShippingInfo] = useState(profile?.shippingInfo);
  const [sizeDetails, setSizeDetails] = useState(profile?.sizeDetails);

  const [localProfile, setLocalProfile] = useState(profile || {});

  const [embeds, setEmbeds] = useState([]);
  const [embedsLoading, setEmbedsLoading] = useState(false);

  const [whichPrice, setWhichPrice] = useState(null);
  const [whichAddon, setWhichAddon] = useState(null);

  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [newPriceItem, setNewPriceItem] = useState(null);
  const [priceModalStage, setPriceModalStage] = useState(0);

  const [addonModalOpen, setAddonModalOpen] = useState(false);
  const [newAddon, setNewAddon] = useState(null);
  const [addonModalStage, setAddonModalStage] = useState(0);

  const [newCaseStudy, setNewCaseStudy] = useState(null);

  const [stripeLinkLoading, setStripeLinkLoading] = useState(false);
  const [addressState, setAddressState] = useState(null);

  const [loading, setLoading] = useState(false);

  const [socialsModalOpen, setSocialsModalOpen] = useState(false);

  const [toast, setToast] = useState(null);
  const toggleActive = useCallback(() => setToast(null), []);
  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.isError} onDismiss={toggleActive} />
  ) : null;

  useEffect(() => {
    setAddressState(profile?.address);
    setLocalProfile(profile);
  }, [profile]);

  async function saveChanges() {
    try {
      setLoading(true);
      await saveInfluencerProfileChanges({
        ...localProfile,
      })
      setToast({
        content: 'Saved Changes',
      })
    } catch (e) {
      console.error(e)
      setToast({
        content: 'Could not save :( Please try again',
        isError: true
      })
    } finally {
      setLoading(false)
    }
  }

  async function saveAddress() {
    try {
      setLoading(true);
      if (addressState?.complete) {
        const addressValue = addressState?.value;
        await saveInfluencerProfileChanges({
          address: addressValue
        })
        setToast({
          content: 'Saved Changes',
        })
      } else {
        setToast({
          content: 'Not a valid address',
          isError: true
        })
      }
    } catch (e) {
      console.error(e)
      setToast({
        content: 'Could not save :( Please try again',
        isError: true
      })
    } finally {
      setLoading(false)
    }
  }

  const detailsChanged = localProfile.openToOffers !== profile?.openToOffers || profile?.audienceDemographics !== localProfile?.audienceDemographics || profile?.age !== localProfile?.age || profile?.gender !== localProfile?.gender

  let tabContent = null;
  if (activeTab === 0) {
    tabContent = (
      <>
        <SocialMediaModal open={socialsModalOpen} onClose={() => setSocialsModalOpen(false)} profile={profile} />
        <VerticalStack gap={{ xs: "8", sm: "4" }}>
          <VerticalStack gap="5">
            <VerticalStack gap="4">
              <VerticalStack gap="8">


                <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
                  <Box
                    as="section"
                    paddingInlineStart={{ xs: 4, sm: 0 }}
                    paddingInlineEnd={{ xs: 4, sm: 0 }}
                  >
                    <VerticalStack gap="4">
                      <Text as="h3" variant="headingMd">
                        Basic Info
                      </Text>
                      <Text as="p" variant="bodyMd">
                        Brand will see this information about you when searching for influencers
                      </Text>
                    </VerticalStack>
                  </Box>
                  <VerticalStack gap="4">
                    {
                      /*
  *
  <AlphaCard roundedAbove="sm">
  {
  profile?.daily && <InfluencerGraph dailyStats={profile?.daily} />
  }
  </AlphaCard>
  */
                    }
                    <VerticalStack gap="5">

                      <HorizontalGrid columns="1fr 4fr" gap="4" alignItems="center">
                        <VerticalStack blockAlign="center">
                          <Image width="100%" source={getBestAvatar(profile)} />
                        </VerticalStack>
                        <VerticalStack gap="3">
                          <TextField
                            label="Name"
                            value={localProfile.name}
                            maxLength={50}
                            onChange={v => setLocalProfile({ ...localProfile, name: v })} />
                          <HorizontalGrid gap="5" columns="2">
                            <Select
                              label="Age"
                              value={localProfile.age}
                              onChange={v => setLocalProfile({ ...localProfile, age: v })}
                              options={[
                                { label: 'Under 18', value: 'Under 18' },
                                { label: '18-24', value: '18-24' },
                                { label: '25-34', value: '25-34' },
                                { label: '35-44', value: '35-44' },
                                { label: '45-54', value: '45-54' },
                                { label: '55-64', value: '55-64' },
                                { label: '65 or Above', value: '65 or Above' },
                              ]} />
                            <Select
                              label="Gender"
                              value={localProfile.gender}
                              onChange={v => setLocalProfile({ ...localProfile, gender: v })}
                              options={[
                                { label: 'Male', value: 'Male' },
                                { label: 'Female', value: 'Female' },
                                { label: 'Other', value: 'other' }
                              ]} />
                          </HorizontalGrid>
                        </VerticalStack>
                      </HorizontalGrid>

                      <VerticalStack gap="3">


                        {/*
                        <AlphaCard>
                          <HorizontalGrid columns={{ xs: "2", md: "3fr 1fr" }} gap="10">
                            <VerticalStack gap="3" align="center">
                              <VerticalStack gap="1">
                                <Text variant="bodyLg">
                                </Text>
                                <Text variant="heading4xl">{profile.name}</Text>
                                <HorizontalStack gap="2">
                                  <HorizontalStack gap="1" blockAlign="end">
                                    <Text variant="bodyLg">{getCountry(profile?.address?.address?.country)}</Text>
                                  </HorizontalStack>
                                </HorizontalStack>

                              </VerticalStack>
                            </VerticalStack>
                            <Image width="100%" source={getBestAvatar(profile)} alt={profile.name} />
                          </HorizontalGrid>
                        </AlphaCard>
                      */}
                      </VerticalStack>
                      <HorizontalGrid columns={{ xs: "1", md: "3" }} gap="3">
                        {
                          Object.keys(profile.socials || {})?.map((platform, i) => {
                            const social = profile.socials[platform];
                            if (!social) return;
                            return (
                              <AlphaCard key={i}>
                                <VerticalStack gap="2">
                                  <HorizontalStack align="space-between" blockAlign="end">
                                    <Text variant="bodyLg">{platform}</Text>
                                    <VerticalStack>
                                      {getSocialIcon(platform)}
                                    </VerticalStack>
                                  </HorizontalStack>
                                  {
                                    social.stats?.followers ? <Text variant="heading2xl">{social.stats?.followers > 1000000 ? (social.stats?.followers / 1000000).toFixed(2) + 'M' : Math.floor(social.stats?.followers / 1000) + 'K'}</Text> : <Text variant="heading2xl">-</Text>
                                  }
                                  {profile?.growth?.followers?.['30'] > 0 ? (
                                    <HorizontalStack gap="1">
                                      {/*
<VerticalStack>
<Icon source={ArrowUpMinor} color="subdued" />
</VerticalStack>
*/}
                                      <Text color="subdued">
                                        {'+' + profile?.growth?.followers?.['30']?.toLocaleString() + " last 30 days"}
                                      </Text>
                                    </HorizontalStack>
                                  ) : (
                                    <HorizontalStack gap="1">
                                      {/*
<VerticalStack>
<Icon source={ArrowDownMinor} color="subdued" />
</VerticalStack>
*/}
                                      <Text color="subdued">
                                        {getSocialLabel(platform, social?.stats)}
                                      </Text>
                                    </HorizontalStack>
                                  )}
                                </VerticalStack>
                              </AlphaCard>
                            )
                          })
                        }
                        {/*
<AlphaCard>
<VerticalStack gap="2">
<HorizontalStack align="space-between">
<Text variant="bodyLg">Platform</Text>
<VerticalStack>
<Icon source={profile.platform === 'instagram' ? ImageMajor : PlayCircleMajor} color="success" />
</VerticalStack>
</HorizontalStack>
<Text variant="heading2xl">{profile.platform}</Text>
<Text color="subdued">{profile?.posts} Posts</Text>
</VerticalStack>
</AlphaCard>
*/}
                      </HorizontalGrid>
                      <Button onClick={() => setSocialsModalOpen(true)}>Edit Social Media</Button>
                      <VerticalStack gap="4">
                        <TextField
                          label="Your Audience Demographics"
                          value={localProfile.audienceDemographics}
                          maxLength={150}
                          multiline={2}
                          showCharacterCount
                          helpText="Be as specific as possible. Try to add gender, country breakdown  & age of your audience"
                          onChange={v => setLocalProfile({ ...localProfile, audienceDemographics: v })} />
                        <ChoiceList
                          title={"I'm Open to Offers From Other Brands"}
                          choices={[
                            { label: 'Yes', value: 'Yes' },
                            { label: 'No', value: 'No' },
                          ]}
                          selected={localProfile.openToOffers || []}
                          onChange={(v) => setLocalProfile({ ...localProfile, openToOffers: v[0] })}
                        />
                        <HorizontalGrid columns={{ xs: "1", md: "3" }} gap="5">
                          <Button onClick={saveChanges} loading={loading} disabled={!detailsChanged} variant="primary">Save Changes</Button>
                        </HorizontalGrid>
                      </VerticalStack>


                    </VerticalStack>

                  </VerticalStack>
                </HorizontalGrid>



              </VerticalStack>
            </VerticalStack>
          </VerticalStack>
          <VerticalStack gap="8">
          </VerticalStack>
        </VerticalStack>
      </>
    )
  } else if (activeTab === 1) {
    tabContent = (
      <>
        <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
          <Box
            as="section"
            paddingInlineStart={{ xs: 4, sm: 0 }}
            paddingInlineEnd={{ xs: 4, sm: 0 }}
          >
            <VerticalStack gap="4">
              <Text as="h3" variant="headingMd">
                Content Portfolio [{caseStudies?.length || 0} / 8]
              </Text>
              <Text as="p" variant="bodyMd">
                Show off your past work with brands. Tag content to get discovered.
              </Text>
            </VerticalStack>
          </Box>
          <VerticalStack gap="4">
            <VerticalStack gap="3">
              <VerticalStack gap="3">
                {
                  caseStudies.map((caseStudy, index) => {
                    return (
                      <AlphaCard key={index}>
                        <VerticalStack gap="4">
                          <HorizontalStack align="space-between">
                            <Box padding="5">
                              <Badge status="success">{caseStudy?.category}</Badge>
                            </Box>
                            <ButtonGroup>
                              <Button icon={DeleteMajor} onClick={() => deleteCaseStudy(caseStudy.id)} />
                            </ButtonGroup>
                          </HorizontalStack>
                          {embeds?.find(embed => embed?.id === caseStudy?.id)?.code}
                        </VerticalStack>
                      </AlphaCard>
                    )
                  }
                  )
                }
              </VerticalStack>


            </VerticalStack>
          </VerticalStack>
        </HorizontalGrid>
      </>
    )
  } else if (activeTab === false) {
    tabContent = (
      <VerticalStack gap="10">
        <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
          <Box
            as="section"
            paddingInlineStart={{ xs: 4, sm: 0 }}
            paddingInlineEnd={{ xs: 4, sm: 0 }}
          >
            <VerticalStack gap="4">
              <Text as="h3" variant="headingMd">
                Prices
              </Text>
              <Text as="p" variant="bodyMd">
                What can brands buy from you?
              </Text>
              <HorizontalStack>
                <Button primary onClick={() => setPriceModalOpen(true)}>Add New Price</Button>
              </HorizontalStack>
            </VerticalStack>
          </Box>
          <VerticalStack gap="4">
            <VerticalStack>
              <VerticalStack gap="3">
                <HorizontalGrid columns={{ xs: "1", md: "2" }} gap="3">
                  {
                    prices.map((product, index) => {
                      return (
                        <AlphaCard key={product.id}>
                          <VerticalStack gap="3">
                            <HorizontalStack blockAlign="start" align="space-between">
                              <VerticalStack>
                                {
                                  Number(product.price) === 0 ? (

                                    <Text variant="headingLg">For Free Product(s)</Text>
                                  ) : (
                                    <Text variant="headingLg">${Number(product.price).toLocaleString()}</Text>
                                  )
                                }
                                <HorizontalStack blockAlign="center" gap="1">
                                  <Text variant="bodyLg" >{product.title}</Text>
                                </HorizontalStack>
                              </VerticalStack>
                              <Box>
                                {
                                  product.piecesOfContent && product.piecesOfContent > 1 ? (
                                    <Icon source={Columns3Major} color="success" />
                                  ) : <Icon source={PlayCircleMajor} color="success" />
                                }
                                <Link removeUnderline onClick={() => {
                                  setPriceModalStage(2);
                                  setNewPriceItem({ ...product })
                                  setPriceModalOpen(true);
                                  setWhichPrice(index);
                                }}>Edit</Link>
                              </Box>
                            </HorizontalStack>

                            <VerticalStack gap="3">
                              <VerticalStack inlineAlign="start" gap="3">
                                <Text variant="bodyMd" color="subdued">🎥 {product.piecesOfContent} Piece(s) of Content</Text>
                                <Text variant="bodyMd" color="subdued">🚚 {product.deliveryTime} Day Deadline</Text>
                                <Text variant="bodyMd" color="subdued">🔁 {product.revisions} Revision(s)</Text>
                              </VerticalStack>
                            </VerticalStack>

                          </VerticalStack>
                        </AlphaCard>
                      )
                    }
                    )
                  }
                </HorizontalGrid>
              </VerticalStack>

            </VerticalStack>
          </VerticalStack>
        </HorizontalGrid>
        <Divider />
        <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
          <Box
            as="section"
            paddingInlineStart={{ xs: 4, sm: 0 }}
            paddingInlineEnd={{ xs: 4, sm: 0 }}
          >
            <VerticalStack gap="4">
              <Text as="h3" variant="headingMd">
                Addons
              </Text>
              <Text as="p" variant="bodyMd">
                Options which can be added to any order
              </Text>
              <HorizontalStack>
                <Button primary onClick={() => setAddonModalOpen(true)}>Add New Addon</Button>
              </HorizontalStack>
            </VerticalStack>
          </Box>
          <VerticalStack gap="4">
            <VerticalStack>
              <VerticalStack gap="3">
                <HorizontalGrid columns={{ xs: "1", md: "2" }} gap="3">
                  {
                    addons.map((addon, index) => {
                      return (
                        <AlphaCard key={index}>
                          <VerticalStack gap="3">
                            <HorizontalStack blockAlign="start" align="space-between">
                              <VerticalStack>
                                <Text color="success" variant="headingLg">+${Number(addon.price).toLocaleString()}</Text>
                                <HorizontalStack blockAlign="center" gap="1">
                                  <Text variant="bodyLg" >{addon.title}</Text>
                                </HorizontalStack>
                              </VerticalStack>
                              <Box>
                                <Link removeUnderline onClick={() => {
                                  setAddonModalStage(2);
                                  setNewAddon({ ...addon })
                                  setAddonModalOpen(true);
                                  setWhichAddon(index);
                                }}>Edit</Link>
                              </Box>
                            </HorizontalStack>

                            <VerticalStack gap="3">
                              <VerticalStack inlineAlign="start" gap="3">
                                {
                                  addon.description
                                }
                              </VerticalStack>
                            </VerticalStack>
                          </VerticalStack>
                        </AlphaCard>
                      )
                    }
                    )
                  }
                </HorizontalGrid>
              </VerticalStack>

            </VerticalStack>
          </VerticalStack>
        </HorizontalGrid>
      </VerticalStack>
    )
  } else if (activeTab === false) {
    tabContent = (
      <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
        <Box
          as="section"
          paddingInlineStart={{ xs: 4, sm: 0 }}
          paddingInlineEnd={{ xs: 4, sm: 0 }}
        >
          <VerticalStack gap="4">
            <Text as="h3" variant="headingMd">
              Payments
            </Text>
            <Text as="p" variant="bodyMd">
              Payouts, Current and Pending Revenue.
            </Text>
          </VerticalStack>
        </Box>
        <VerticalStack gap="4">
          {
            /*
*
<AlphaCard roundedAbove="sm">
{
profile?.daily && <InfluencerGraph dailyStats={profile?.daily} />
}
</AlphaCard>
*/
          }
          <VerticalStack>
            <VerticalStack gap="3">
              <HorizontalGrid columns={{ xs: "1", md: "3" }} gap="5">
                <Button disabled={!profile.stripeAccountSetupComplete} loading={stripeLinkLoading} onClick={async () => {
                  setStripeLinkLoading(true)
                  try {
                    // const { data: login } = await getStripeLoginLink();
                    window.location.href = login.url
                  } catch (e) {
                    console.error('error', e);
                    setStripeLinkLoading(false)
                  }
                }}>
                  <HorizontalStack blockAlign="center">
                    <Text>
                      Open
                    </Text>
                    {!stripeLinkLoading && <Image source={stripeLogo} width="60px" alt="Stripe" />}
                  </HorizontalStack></Button>
              </HorizontalGrid>
              {stripeLinkLoading && <ExceptionList items={[{ icon: ClockMinor, description: 'Connecting to Stripe, this can take up to 30 seconds.' }]} />}
              {!profile.stripeAccountSetupComplete && <InlineError message="Set up Stripe from the Home tab" />}
            </VerticalStack>
          </VerticalStack>
        </VerticalStack>
      </HorizontalGrid >
    )
  } else if (activeTab === 2) {

    tabContent = (
      <HorizontalGrid columns={{ xs: "1fr", md: "1fr 2fr" }} gap="4">
        <Box
          as="section"
          paddingInlineStart={{ xs: 4, sm: 0 }}
          paddingInlineEnd={{ xs: 4, sm: 0 }}
        >
          <VerticalStack gap="4">
            <Text as="h3" variant="headingMd">
              Your Address
            </Text>
            <Text as="p" variant="bodyMd">
              Your personal details are encrypted and only used for shipping, nothing else.
            </Text>
          </VerticalStack>
        </Box>
        <VerticalStack gap="4">
          {
            /*
*
<AlphaCard roundedAbove="sm">
{
profile?.daily && <InfluencerGraph dailyStats={profile?.daily} />
}
</AlphaCard>
*/
          }
          <VerticalStack gap="5">
            <AddressElement
              onChange={v => {
                console.log('setting address 2', v)
                setAddressState(v)
              }}
              options={{
                mode: 'shipping',
                autocomplete: {
                  mode: 'google_maps_api',
                  apiKey: MAPS_KEY,
                },
                defaultValues: profile?.address || {}
              }} />
            <HorizontalGrid columns={{ xs: "1", md: "3" }} gap="5">
              <Button
                disabled={!addressState?.complete}
                loading={loading}
                onClick={saveAddress}>
                Update Address
              </Button>
            </HorizontalGrid>
            <Box />
            <Box />
          </VerticalStack>
        </VerticalStack>
        <Box
          as="section"
          paddingInlineStart={{ xs: 4, sm: 0 }}
          paddingInlineEnd={{ xs: 4, sm: 0 }}
        >
          <VerticalStack gap="4">
            <Text as="h3" variant="headingMd">
              Your Sizing Details
            </Text>
            <Text as="p" variant="bodyMd">
              This is used to help brands send you the right size.
            </Text>
          </VerticalStack>
        </Box>
        <VerticalStack gap="4">
          {
            /*
*
<AlphaCard roundedAbove="sm">
{
profile?.daily && <InfluencerGraph dailyStats={profile?.daily} />
}
</AlphaCard>
*/
          }
          <VerticalStack gap="5">
            <TextField multiline={3} helpText="Ex Men's M, 32 waist, US 9 Shoes" label="Sizing details" value={sizeDetails} onChange={v => {
              setSizeDetails(v)
            }} />
            <HorizontalGrid columns={{ xs: "1", md: "3" }} gap="5">
              <Button
                disabled={profile?.sizeDetails === sizeDetails}
                loading={loading}
                onClick={saveChanges}>
                Save
              </Button>
            </HorizontalGrid>
            <Box />
            <Box />
          </VerticalStack>
        </VerticalStack>
      </HorizontalGrid>
    )
  }
  return (
    <Layout>
      {toastMarkup}
      <Page
        title="Profile"
        divider
      >
        <PriceModal
          open={priceModalOpen}
          setOpen={setPriceModalOpen}
          price={prices?.[whichPrice]}
          priceModalStage={priceModalStage}
          setPriceModalStage={setPriceModalStage}
          newPriceItem={newPriceItem}
          setNewPriceItem={setNewPriceItem}
          platform={profile?.platform}
        />
        <AddonModal
          open={addonModalOpen}
          setOpen={setAddonModalOpen}
          addon={addons?.[whichAddon]}
          addonModalStage={addonModalStage}
          setAddonModalStage={setAddonModalStage}
          newAddon={newAddon}
          setNewAddon={setNewAddon}
        />
        <VerticalStack gap="4">
          {/*
          <Button onClick={testGetIGData}>Get IG Data</Button>
        */}
          <Tabs tabs={[
            {
              id: 'profile',
              content: 'Basic Info',
            },
            {
              id: 'case-studies',
              content: 'Your Portfolio'
            },
            /*
            {
              id: 'prices',
              content: 'Prices & Addons',
            },
            */
            /*
            {
              id: 'revenue',
              content: 'Revenue',
            },
            */
            {
              id: 'address',
              content: 'Address & Sizing',
            }
          ]}
            selected={activeTab}
            onSelect={t => setActiveTab(t)}
          />
          <Box padding={{ xs: "5", md: "0" }}>
            {tabContent}
          </Box>
          <Box />
          <Box />
        </VerticalStack>
      </Page >
    </Layout >
  )
}
